import React from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { colors, fontSize, fontWeight } from '../../style/tokens';

/** One collapsible block of indicator copy. */
export interface DetailBlock {
  /** i18n key for the block heading (e.g. metric.ui.description). */
  labelKey: string;
  /**
   * Body copy, already resolved to the session language. Not narrowed to
   * `string`: formatMessage returns a node list for messages with rich-text
   * placeholders.
   */
  text: React.ReactNode;
  variant?: 'advice' | 'tip';
}

interface DetailBlocksProps {
  blocks: DetailBlock[];
  /**
   * Index of the block expanded on mount; null collapses them all.
   * Defaults to the first block, which is the one that carries the
   * Protocol v2.0 description.
   */
  initialOpen?: number | null;
  /**
   * metric.ui.disclaimer under the blocks. The detail pages show it; the
   * report page passes false because it renders its own disclaimer.
   */
  showDisclaimer?: boolean;
  /** Drop the side padding when the parent already provides the page gutter. */
  flush?: boolean;
}

// ── Layout ────────────────────────────────────────────────────────────────────

const InfoBlocks = styled.div<{ $flush?: boolean }>`
  padding: ${({ $flush }) => ($flush ? '10px 0 0' : '16px 14px')};
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const InfoBlock = styled.div<{ variant?: 'advice' | 'tip' }>`
  background: ${({ variant }) => (variant === 'tip' ? '#f4fce8' : colors.bg)};
  border: 1.5px solid ${({ variant, theme }) =>
    variant === 'tip' ? '#c8e89a' : theme.gradient1};
  border-radius: 14px;
  padding: 16px 18px;
`;

const BlockHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: 'Outfit', sans-serif;
  text-align: left;
`;

const BlockLabel = styled.div`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${p => p.theme.gradient1};
`;

// Same chevron as the top-navigation back button, rotated to point
// down (collapsed) / up (expanded).
const Chevron = styled.span<{ $open: boolean }>`
  width: 9px;
  height: 9px;
  flex-shrink: 0;
  border-top: 2px solid #333;
  border-left: 2px solid #333;
  transform: rotate(${({ $open }) => ($open ? '45deg' : '-135deg')});
  transition: transform 0.28s ease;
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

// grid-rows 0fr↔1fr gives a smooth height animation for variable content.
const Collapsible = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  transition: grid-template-rows 0.28s ease;
  @media (prefers-reduced-motion: reduce) { transition: none; }
  > div { overflow: hidden; }
`;

// Content-API descriptions arrive as plain text with embedded newlines —
// preserve them.
const BlockText = styled.p`
  font-size: ${fontSize.base};
  color: ${colors.text};
  line-height: 1.65;
  padding-top: 10px;
  white-space: pre-line;
`;

const Disclaimer = styled.p`
  font-size: ${fontSize.xs};
  color: #aaa;
  text-align: center;
  padding: 0 20px 8px;
  line-height: 1.55;
`;

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * The accordion of indicator copy, shared by the metric / CE detail pages and
 * by the report page's inline single-indicator layout, so all three stay
 * identical.
 */
const DetailBlocks: React.FC<DetailBlocksProps> = ({
  blocks,
  initialOpen = 0,
  showDisclaimer = true,
  flush = false,
}) => {
  const intl = useIntl();
  const [openBlock, setOpenBlock] = React.useState<number | null>(initialOpen);
  const toggleBlock = (i: number) => setOpenBlock(prev => (prev === i ? null : i));

  return (
    <InfoBlocks $flush={flush}>
      {blocks.map((block, i) => (
        <InfoBlock key={block.labelKey} variant={block.variant}>
          <BlockHeader onClick={() => toggleBlock(i)} aria-expanded={openBlock === i}>
            <BlockLabel>{intl.formatMessage({ id: block.labelKey })}</BlockLabel>
            <Chevron $open={openBlock === i} />
          </BlockHeader>
          <Collapsible $open={openBlock === i}>
            <div>
              <BlockText>{block.text}</BlockText>
            </div>
          </Collapsible>
        </InfoBlock>
      ))}

      {showDisclaimer && (
        <Disclaimer>{intl.formatMessage({ id: 'metric.ui.disclaimer' })}</Disclaimer>
      )}
    </InfoBlocks>
  );
};

export default DetailBlocks;
